import React, { useState, useRef, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from './common/SafeIcon';
import Papa from 'papaparse';

const { 
  FiCode, FiEye, FiCopy, FiMail, FiCheck, FiDownload, 
  FiUsers, FiUserPlus, FiUpload, FiTrash2, FiChevronLeft, 
  FiChevronRight, FiExternalLink, FiPlus 
} = FiIcons;

function App() {
  // Original State
  const [htmlCode, setHtmlCode] = useState('');
  const [copied, setCopied] = useState(false);

  // New State for Contact Management
  const [contacts, setContacts] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [processedIndices, setProcessedIndices] = useState(new Set());
  const [contactInput, setContactInput] = useState('');
  const [subject, setSubject] = useState('');
  const [showSubjectPreview, setShowSubjectPreview] = useState(false);
  
  const fileInputRef = useRef(null);
  const htmlTextAreaRef = useRef(null);

  // Derived Data
  const currentContact = contacts[currentIndex];
  
  const mergedHTML = useMemo(() => {
    if (!currentContact || !htmlCode) return htmlCode;
    let merged = htmlCode;
    merged = merged.replace(/{{Name}}/g, currentContact.name || '');
    merged = merged.replace(/{{Email}}/g, currentContact.email || '');
    return merged;
  }, [htmlCode, currentContact]);

  const mergedSubject = useMemo(() => {
    if (!currentContact || !subject) return subject;
    let merged = subject;
    merged = merged.replace(/{{Name}}/g, currentContact.name || '');
    merged = merged.replace(/{{Email}}/g, currentContact.email || '');
    return merged;
  }, [subject, currentContact]);

  // Contact Management Functions
  const handleAddContacts = () => {
    if (!contactInput.trim()) return;
    
    const lines = contactInput.split('\n');
    const newContacts = lines
      .map(line => {
        const parts = line.split(',');
        if (parts.length >= 2) {
          return {
            name: parts[0].trim(),
            email: parts[1].trim()
          };
        }
        return null;
      })
      .filter(Boolean);

    if (newContacts.length > 0) {
      setContacts(prev => [...prev, ...newContacts]);
      setContactInput('');
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      Papa.parse(file, {
        header: true,
        complete: (results) => {
          const newContacts = results.data
            .filter(row => row.Name && row.Email)
            .map(row => ({
              name: row.Name.trim(),
              email: row.Email.trim()
            }));
          
          if (newContacts.length > 0) {
            setContacts(prev => [...prev, ...newContacts]);
          }
        }
      });
    }
    // Reset file input
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeContact = (indexToRemove) => {
    setContacts(prev => prev.filter((_, idx) => idx !== indexToRemove));
    if (currentIndex >= indexToRemove && currentIndex > 0) {
      setCurrentIndex(prev => prev - 1);
    }
    // Update processed indices
    const newProcessed = new Set();
    Array.from(processedIndices).forEach(idx => {
      if (idx < indexToRemove) newProcessed.add(idx);
      if (idx > indexToRemove) newProcessed.add(idx - 1);
    });
    setProcessedIndices(newProcessed);
  };

  // Merge Field Insertion
  const insertMergeField = (field) => {
    const textarea = htmlTextAreaRef.current;
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const text = htmlCode;
      const before = text.substring(0, start);
      const after = text.substring(end, text.length);
      const newText = before + `{{${field}}}` + after;
      setHtmlCode(newText);
      
      // Restore focus and cursor position
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(start + field.length + 4, start + field.length + 4);
      }, 0);
    } else {
      setHtmlCode(prev => prev + `{{${field}}}`);
    }
  };

  // Original Copy Function (Modified to accept content)
  const handleCopyForGmail = async (contentToCopy = htmlCode) => {
    try {
      // Create a temporary div to render the HTML
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = contentToCopy;
      
      // Create a range and selection to copy the rendered content
      const range = document.createRange();
      range.selectNodeContents(tempDiv);
      
      const selection = window.getSelection();
      selection.removeAllRanges();
      selection.addRange(range);
      
      // Copy the formatted content
      await navigator.clipboard.write([
        new ClipboardItem({
          'text/html': new Blob([contentToCopy], { type: 'text/html' }),
          'text/plain': new Blob([tempDiv.textContent], { type: 'text/plain' })
        })
      ]);
      
      // Clean up
      selection.removeAllRanges();
      tempDiv.remove();
      
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      // Fallback for browsers that don't support ClipboardItem
      try {
        await navigator.clipboard.writeText(contentToCopy);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (fallbackError) {
        console.error('Failed to copy:', fallbackError);
      }
    }
  };

  const handleCopyAndOpenGmail = async () => {
    if (!currentContact) return;
    
    // Copy merged content
    await handleCopyForGmail(mergedHTML);
    
    // Mark as processed
    setProcessedIndices(prev => new Set(prev).add(currentIndex));
    
    // Open Gmail
    const gmailUrl = `https://mail.google.com/mail/?view=cm&to=${encodeURIComponent(currentContact.email)}&su=${encodeURIComponent(mergedSubject)}`;
    window.open(gmailUrl, '_blank');
  };

  const handleDownloadHTML = () => {
    if (!mergedHTML.trim()) return;
    
    // Create a complete HTML document
    const fullHTML = `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>${mergedSubject || 'HTML Export'}</title>
</head>
<body>
${mergedHTML}
</body>
</html>`;
    
    // Create blob and download
    const blob = new Blob([fullHTML], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `email-export-${currentContact ? currentContact.name.replace(/\s+/g, '-') : 'template'}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const sampleHTML = `<div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px; background-color: #f8f9fa; border-radius: 8px;">
  <h2 style="color: #2563eb; margin-bottom: 16px;">Hello {{Name}}!</h2>
  <p style="color: #374151; line-height: 1.6; margin-bottom: 16px;">
    We are excited to contact you at {{Email}}. This is a sample email template that you can customize.
  </p>
  <div style="background-color: white; padding: 20px; border-radius: 6px; margin: 16px 0;">
    <h3 style="color: #1f2937; margin-bottom: 12px;">Key Features:</h3>
    <ul style="color: #4b5563; line-height: 1.8;">
      <li>Easy to customize</li>
      <li>Gmail-friendly formatting</li>
      <li>Responsive design</li>
    </ul>
  </div>
  <p style="color: #374151; line-height: 1.6;">
    Best regards,<br>
    <strong>Your Team</strong>
  </p>
</div>`;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4 font-sans">
      <div className="max-w-[1600px] mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <h1 className="text-4xl font-bold text-gray-800 mb-2">
            Email Campaign Manager
          </h1>
          <p className="text-gray-600">
            Manage contacts, personalize templates, and send via Gmail
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 h-full">
          
          {/* LEFT COLUMN: Contact Management */}
          <motion.div 
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="lg:col-span-4 flex flex-col gap-6"
          >
            {/* Contact Input */}
            <div className="bg-white rounded-xl shadow-lg p-6 flex-shrink-0">
              <div className="flex items-center gap-2 mb-4">
                <SafeIcon icon={FiUsers} className="text-blue-500 text-xl" />
                <h2 className="text-xl font-semibold text-gray-800">Add Contacts</h2>
              </div>
              
              <textarea
                value={contactInput}
                onChange={(e) => setContactInput(e.target.value)}
                placeholder="Paste contacts here...&#10;John Doe, john@example.com&#10;Jane Smith, jane@test.com"
                className="w-full h-32 p-3 border border-gray-200 rounded-lg text-sm resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent mb-3"
              />
              
              <div className="flex gap-2">
                <button
                  onClick={handleAddContacts}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors text-sm font-medium"
                >
                  <SafeIcon icon={FiUserPlus} />
                  Add Contacts
                </button>
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="flex items-center justify-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium"
                >
                  <SafeIcon icon={FiUpload} />
                  CSV
                </button>
                <input
                  type="file"
                  ref={fileInputRef}
                  className="hidden"
                  accept=".csv"
                  onChange={handleFileUpload}
                />
              </div>
            </div>

            {/* Contact List */}
            <div className="bg-white rounded-xl shadow-lg p-6 flex-1 flex flex-col min-h-[400px]">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-gray-800">Contact List</h2>
                <span className="text-sm font-medium text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
                  {contacts.length} Total
                </span>
              </div>
              
              <div className="flex-1 overflow-y-auto pr-2 space-y-2 max-h-[500px]">
                {contacts.length === 0 ? (
                  <div className="text-center text-gray-400 py-8">
                    <SafeIcon icon={FiUsers} className="text-4xl mx-auto mb-2 opacity-50" />
                    <p>No contacts added yet</p>
                  </div>
                ) : (
                  contacts.map((contact, idx) => (
                    <div 
                      key={idx}
                      onClick={() => setCurrentIndex(idx)}
                      className={`p-3 rounded-lg border cursor-pointer transition-all flex items-center justify-between group ${
                        idx === currentIndex 
                          ? 'bg-blue-50 border-blue-200 ring-1 ring-blue-200' 
                          : 'border-gray-100 hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center gap-3 overflow-hidden">
                        <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${
                          processedIndices.has(idx) ? 'bg-green-500' : 'bg-gray-300'
                        }`} />
                        <div className="min-w-0">
                          <p className="font-medium text-gray-800 truncate">{contact.name}</p>
                          <p className="text-xs text-gray-500 truncate">{contact.email}</p>
                        </div>
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          removeContact(idx);
                        }}
                        className="text-gray-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity p-1"
                      >
                        <SafeIcon icon={FiTrash2} />
                      </button>
                    </div>
                  ))
                )}
              </div>
            </div>
          </motion.div>

          {/* RIGHT COLUMN: Editor & Preview */}
          <div className="lg:col-span-8 flex flex-col gap-6">
            
            {/* HTML Editor Section */}
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-xl shadow-lg p-6"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <SafeIcon icon={FiCode} className="text-blue-500 text-xl" />
                  <h2 className="text-xl font-semibold text-gray-800">Email Template</h2>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => insertMergeField('Name')} className="text-xs px-3 py-1.5 bg-indigo-50 text-indigo-600 rounded-md hover:bg-indigo-100 font-medium flex items-center gap-1">
                    <SafeIcon icon={FiPlus} className="text-xs" /> Insert Name
                  </button>
                  <button onClick={() => insertMergeField('Email')} className="text-xs px-3 py-1.5 bg-indigo-50 text-indigo-600 rounded-md hover:bg-indigo-100 font-medium flex items-center gap-1">
                    <SafeIcon icon={FiPlus} className="text-xs" /> Insert Email
                  </button>
                </div>
              </div>

              {/* Subject Line Input */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Subject Line</label>
                <input
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="Welcome {{Name}} to our newsletter!"
                  className="w-full p-3 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              
              <textarea
                ref={htmlTextAreaRef}
                value={htmlCode}
                onChange={(e) => setHtmlCode(e.target.value)}
                placeholder="Paste your HTML code here..."
                className="w-full h-64 p-4 border border-gray-200 rounded-lg font-mono text-sm resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              
              <div className="flex gap-3 mt-4 justify-end">
                <button
                  onClick={() => setHtmlCode(sampleHTML)}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm"
                >
                  Load Sample
                </button>
                <button
                  onClick={() => setHtmlCode('')}
                  className="px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors text-sm"
                >
                  Clear
                </button>
              </div>
            </motion.div>

            {/* Navigation Bar */}
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.15 }}
              className="bg-white rounded-lg shadow-sm p-3 flex items-center justify-between"
            >
              <button
                onClick={() => setCurrentIndex(prev => Math.max(0, prev - 1))}
                disabled={contacts.length === 0 || currentIndex === 0}
                className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-gray-600 hover:text-blue-600 disabled:opacity-30 disabled:hover:text-gray-600"
              >
                <SafeIcon icon={FiChevronLeft} /> Previous
              </button>
              
              <span className="text-sm font-medium text-gray-700">
                {contacts.length > 0 
                  ? `Contact ${currentIndex + 1} of ${contacts.length}`
                  : 'No contacts selected'}
              </span>
              
              <button
                onClick={() => setCurrentIndex(prev => Math.min(contacts.length - 1, prev + 1))}
                disabled={contacts.length === 0 || currentIndex === contacts.length - 1}
                className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-gray-600 hover:text-blue-600 disabled:opacity-30 disabled:hover:text-gray-600"
              >
                Next <SafeIcon icon={FiChevronRight} />
              </button>
            </motion.div>

            {/* Preview Section */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-xl shadow-lg p-6 flex-1 flex flex-col"
            >
              <div className="flex items-center justify-between mb-4 flex-wrap gap-4">
                <div className="flex items-center gap-2">
                  <SafeIcon icon={FiEye} className="text-green-500 text-xl" />
                  <h2 className="text-xl font-semibold text-gray-800">Preview</h2>
                </div>
                
                <div className="flex gap-2 flex-wrap">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={handleDownloadHTML}
                    disabled={!mergedHTML.trim()}
                    className="flex items-center gap-2 px-3 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 disabled:bg-gray-300 disabled:cursor-not-allowed font-medium text-sm transition-all"
                  >
                    <SafeIcon icon={FiDownload} />
                    Download
                  </motion.button>
                  
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => handleCopyForGmail(mergedHTML)}
                    disabled={!mergedHTML.trim()}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg font-medium text-sm transition-all ${
                      copied
                        ? 'bg-green-500 text-white'
                        : 'bg-blue-500 text-white hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed'
                    }`}
                  >
                    <SafeIcon 
                      icon={copied ? FiCheck : FiMail} 
                    />
                    {copied ? 'Copied!' : 'Copy for Gmail'}
                  </motion.button>

                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={handleCopyAndOpenGmail}
                    disabled={!mergedHTML.trim() || !currentContact}
                    className="flex items-center gap-2 px-3 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed font-medium text-sm transition-all"
                  >
                    <SafeIcon icon={FiExternalLink} />
                    Copy & Open Gmail
                  </motion.button>
                </div>
              </div>

              {/* Subject Preview */}
              {mergedSubject && (
                <div className="mb-4 p-3 bg-gray-50 border border-gray-200 rounded-lg text-sm">
                  <span className="font-semibold text-gray-500">Subject: </span>
                  <span className="text-gray-800 font-medium">{mergedSubject}</span>
                </div>
              )}
              
              <div className="border border-gray-200 rounded-lg flex-1 min-h-[400px] overflow-auto bg-gray-50">
                {mergedHTML.trim() ? (
                  <div
                    className="p-4"
                    dangerouslySetInnerHTML={{ __html: mergedHTML }}
                  />
                ) : (
                  <div className="flex items-center justify-center h-full text-gray-400">
                    <div className="text-center">
                      <SafeIcon icon={FiCode} className="text-4xl mx-auto mb-2" />
                      <p>Paste HTML code to see preview</p>
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default App;